/**
 * useAutoProposal — Hook for the 3-tier auto-proposal flow
 *
 * Manages state for:
 * 1. Building 3 scenarios (auto-propose)
 * 2. Submitting each for optimization (via existing /submit endpoint)
 * 3. Polling status for all 3 runs
 * 4. Aggregating results
 */

import { useState, useCallback, useRef } from 'react';
import { useAuth } from '@vortx/shared-ui';
import { autoPropose } from '../services/partnerService';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const POLL_INTERVAL_MS = 3000;

export function useAutoProposal() {
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('idle'); // idle | proposing | submitting | polling | done | error
  const [proposalData, setProposalData] = useState(null);
  const [runs, setRuns] = useState([]); // [{ tier_key, run_uuid, status, results }]
  const [error, setError] = useState(null);
  const pollRef = useRef(null);

  const reset = useCallback(() => {
    setStatus('idle');
    setProposalData(null);
    setRuns([]);
    setError(null);
    if (pollRef.current) clearInterval(pollRef.current);
  }, []);

  /**
   * Submit a single scenario's reopt_inputs to the solver.
   */
  const submitScenario = useCallback(async (reoptInputs) => {
    const token = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/site_dna/submit`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Frontend-Source': 'partner-portal',
      },
      body: JSON.stringify(reoptInputs),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Submit failed: ${res.status}`);
    return data.run_uuid;
  }, [currentUser]);

  /**
   * Poll status for a single run UUID.
   */
  const pollRunStatus = useCallback(async (runUuid) => {
    const token = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/site_dna/status/${runUuid}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return { status: 'error' };
    return res.json();
  }, [currentUser]);

  /**
   * Get results for a completed run.
   */
  const getRunResults = useCallback(async (runUuid) => {
    const token = await currentUser.getIdToken();
    const res = await fetch(`${API_BASE_URL}/api/site_dna/results/${runUuid}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) return null;
    return res.json();
  }, [currentUser]);

  /**
   * Main flow: propose → submit 3 → poll until all done.
   */
  const startProposal = useCallback(async ({ address, lat, lon, loads_kw, tariff_data, customer_name, curve_id }) => {
    try {
      setError(null);

      // Step 1: Auto-propose (build 3 scenarios in Firestore)
      setStatus('proposing');
      const proposal = await autoPropose(currentUser, {
        address, lat, lon, loads_kw, tariff_data, customer_name, curve_id,
      });
      setProposalData(proposal);

      // Step 2: Submit each scenario for optimization
      setStatus('submitting');
      const runEntries = [];
      for (const scenario of proposal.scenarios) {
        try {
          const runUuid = await submitScenario(scenario.reopt_inputs);
          runEntries.push({
            tier_key: scenario.tier_key,
            tier_label: scenario.tier_label,
            scenario_id: scenario.scenario_id,
            run_uuid: runUuid,
            status: 'submitted',
            results: null,
          });
        } catch (submitErr) {
          runEntries.push({
            tier_key: scenario.tier_key,
            tier_label: scenario.tier_label,
            scenario_id: scenario.scenario_id,
            run_uuid: null,
            status: 'error',
            error: submitErr.message,
            results: null,
          });
        }
      }
      setRuns(runEntries);

      // Step 3: Poll until all runs complete
      setStatus('polling');
      pollRef.current = setInterval(async () => {
        let allDone = true;
        const updated = await Promise.all(
          runEntries.map(async (run) => {
            if (!run.run_uuid || run.status === 'error' || run.status === 'complete') {
              return run;
            }
            const statusData = await pollRunStatus(run.run_uuid);
            const runStatus = statusData?.status;

            if (runStatus === 'optimal' || runStatus === 'complete') {
              const results = await getRunResults(run.run_uuid);
              return { ...run, status: 'complete', results };
            } else if (runStatus === 'error' || runStatus === 'infeasible') {
              return { ...run, status: 'error', error: statusData?.message || 'Optimization failed' };
            } else {
              allDone = false;
              return { ...run, status: runStatus || 'running' };
            }
          }),
        );

        setRuns(updated);
        runEntries.splice(0, runEntries.length, ...updated);

        if (allDone) {
          clearInterval(pollRef.current);
          pollRef.current = null;
          setStatus('done');
        }
      }, POLL_INTERVAL_MS);

    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }, [currentUser, submitScenario, pollRunStatus, getRunResults]);

  return {
    status,
    proposalData,
    runs,
    error,
    startProposal,
    reset,
  };
}
